import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import type { ReactNode } from 'react';
import { comprobantesService } from '@/services/api/comprobantes.service';
import type { ComprobanteListadoDto } from '@/services/api/comprobantes.service';
import { ComprobantesListCtx } from './ComprobantesListContext';
import type {
  Comprobante,
  ComprobantesFilters,
  ComprobantesListAction,
  ComprobantesListState,
} from './ComprobantesListContext';
import { INITIAL_FILTERS as FILTERS_DEFAULT } from './ComprobantesListContext';
import type { PaginacionInfo } from '@/services/api/api.types';

function toLimaDate(iso: string): string {
  if (iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso)) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Lima' }).format(new Date(iso));
  }
  return iso.slice(0, 10);
}

function mapDtoToComprobante(dto: ComprobanteListadoDto): Comprobante {
  return {
    id: dto.id,
    serie: dto.serie,
    correlativo: dto.correlativo,
    type: dto.tipoDocumentoNombre,
    tipoDocumentoCodigo: dto.tipoDocumentoCodigo,
    date: dto.fechaEmision ?? undefined,
    issueDate: dto.fechaEmision ? toLimaDate(dto.fechaEmision) : undefined,
    dueDate: dto.fechaVencimiento ? toLimaDate(dto.fechaVencimiento) : undefined,
    createdAt: dto.createdAt ? toLimaDate(dto.createdAt) : undefined,
    updatedAt: dto.updatedAt,
    client: dto.clienteNombres,
    clientDoc: dto.clienteNumeroDocumento,
    vendor: dto.vendedorNombre,
    paymentMethod: dto.formaPagoNombre,
    currency: dto.monedaCodigo,
    exchangeRate: dto.tipoCambio,
    total: dto.total,
    status: dto.estado,
    codigoRespuesta: dto.codigoRespuesta,
    mensajeRespuesta: dto.mensajeRespuesta,
    email: dto.correoElectronico,
    address: dto.direccionFacturacion,
    shippingAddress: dto.direccionEnvio,
    purchaseOrder: dto.ordenCompra,
    waybill: dto.numeroGuia,
    observations: dto.observacion,
    internalNote: dto.observacionInterno,
    tipoDocumentoRelacionado: dto.tipoDocumentoRelacionado,
    documentoRelacionado: dto.documentoRelacionado,
    tipoNotaCreditoNombre: dto.tipoNotaCreditoNombre,
    tipoNotaDebitoNombre: dto.tipoNotaDebitoNombre,
    motivoEmisionNc: dto.motivoEmisionNc,
    motivoEmisionNd: dto.motivoEmisionNd,
    tieneMovimientoStock: dto.tieneMovimientoStock,
    tieneNotaSalidaActiva: dto.tieneNotaSalidaActiva,
    origenVenta: dto.origenVenta,
  };
}

const DEFAULT_PAGINACION: PaginacionInfo = {
  page: 1,
  pageSize: 50,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
};

const initialState: ComprobantesListState = {
  comprobantes: [],
  esCargando: false,
  error: null,
  filters: FILTERS_DEFAULT,
  page: 1,
  pageSize: 50,
  paginacion: DEFAULT_PAGINACION,
  lastFetchedAt: null,
};

function comprobantesReducer(
  state: ComprobantesListState,
  action: ComprobantesListAction,
): ComprobantesListState {
  switch (action.type) {
    case 'SET_COMPROBANTES':
      return { ...state, comprobantes: action.payload ?? [], esCargando: false };
    case 'SET_PAGINACION':
      return { ...state, paginacion: action.payload };
    case 'ADD_COMPROBANTE':
      return { ...state, comprobantes: [action.payload, ...state.comprobantes] };
    case 'UPDATE_COMPROBANTE': {
      const updated = action.payload as Comprobante;
      return {
        ...state,
        comprobantes: state.comprobantes.map(c =>
          c.id === updated.id ? { ...c, ...updated } : c,
        ),
      };
    }
    case 'SET_LOADING':
      return { ...state, esCargando: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, esCargando: false };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload }, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, page: 1 };
    case 'SET_LAST_FETCHED':
      return { ...state, lastFetchedAt: action.payload };
    default:
      return state;
  }
}

interface ComprobantesListProviderProps {
  children: ReactNode;
  filtrosIniciales?: Partial<ComprobantesFilters>;
  estadosFijos?: string[];
}

export function ComprobantesListProvider({ children, filtrosIniciales, estadosFijos }: ComprobantesListProviderProps) {
  const [state, dispatch] = useReducer(comprobantesReducer, initialState, (base) => ({
    ...base,
    filters: { ...base.filters, ...filtrosIniciales },
  }));
  const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const prevFiltersRef = useRef(state.filters);
  const prevPageRef = useRef(state.page);
  const prevPageSizeRef = useRef(state.pageSize);

  const fetchComprobantes = useCallback(async (
    filters: ComprobantesFilters,
    page: number,
    pageSize: number,
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const estados = estadosFijos ?? filters.estados;
    try {
      const res = await comprobantesService.getAll({
        page,
        pageSize,
        search: filters.search || undefined,
        estado: estados.length ? estados.join(',') : undefined,
        fechaDesde: filters.fechaDesde || undefined,
        fechaHasta: filters.fechaHasta || undefined,
        tipoDocumento: filters.tipoDocumento || undefined,
        vendedor: filters.vendedor || undefined,
        formaPago: filters.formaPago || undefined,
        totalMin: filters.totalMin ? parseFloat(filters.totalMin) : undefined,
        totalMax: filters.totalMax ? parseFloat(filters.totalMax) : undefined,
      });
      if (res.ok && res.data) {
        dispatch({
          type: 'SET_COMPROBANTES',
          payload: Array.isArray(res.data) ? res.data.map(mapDtoToComprobante) : [],
        });
        if (res.paginacion) {
          dispatch({ type: 'SET_PAGINACION', payload: res.paginacion });
        }
        dispatch({ type: 'SET_LAST_FETCHED', payload: Date.now() });
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch {
      dispatch({ type: 'SET_ERROR', payload: 'Error al cargar comprobantes' });
    }
  }, [estadosFijos]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      void fetchComprobantes(state.filters, state.page, state.pageSize);
      return;
    }

    const filtersChanged = state.filters !== prevFiltersRef.current;
    const pageChanged = state.page !== prevPageRef.current;
    const pageSizeChanged = state.pageSize !== prevPageSizeRef.current;

    prevFiltersRef.current = state.filters;
    prevPageRef.current = state.page;
    prevPageSizeRef.current = state.pageSize;

    if (!filtersChanged && !pageChanged && !pageSizeChanged) return;

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);

    const delay = filtersChanged && !pageChanged && !pageSizeChanged ? 400 : 0;
    fetchTimeoutRef.current = setTimeout(() => {
      void fetchComprobantes(state.filters, state.page, state.pageSize);
    }, delay);

    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };

  }, [state.filters, state.page, state.pageSize]);

  const { establecimientoId } = useWorkspace();
  const establecimientoPrevioRef = useRef(establecimientoId);

  useEffect(() => {
    if (establecimientoPrevioRef.current === establecimientoId) return;
    establecimientoPrevioRef.current = establecimientoId;

    if (state.page !== 1) {
      dispatch({ type: 'SET_PAGE', payload: 1 });
      return;
    }
    void fetchComprobantes(state.filters, 1, state.pageSize);
  }, [establecimientoId]);

  const setFilters = useCallback((partial: Partial<ComprobantesFilters>) => {
    dispatch({ type: 'SET_FILTERS', payload: partial });
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setPageSize = useCallback((size: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size });
  }, []);

  const refreshComprobantes = useCallback(
    () => fetchComprobantes(state.filters, state.page, state.pageSize),
    [fetchComprobantes, state.filters, state.page, state.pageSize],
  );

  const value = useMemo(() => ({
    state,
    dispatch,
    comprobantes: state.comprobantes,
    esCargando: state.esCargando,
    error: state.error,
    paginacion: state.paginacion,
    lastFetchedAt: state.lastFetchedAt,
    addComprobante: (c: Comprobante) => dispatch({ type: 'ADD_COMPROBANTE', payload: c }),
    updateComprobante: (id: string, changes: Partial<Comprobante>) =>
      dispatch({ type: 'UPDATE_COMPROBANTE', payload: { id, ...changes } }),
    refreshComprobantes,
    setFilters,
    setPage,
    setPageSize,
  }), [state, refreshComprobantes, setFilters, setPage, setPageSize]);

  return (
    <ComprobantesListCtx.Provider value={value}>
      {children}
    </ComprobantesListCtx.Provider>
  );
}
